/**
 * ImageConverter — componente reutilizable
 *
 * Convierte cualquier imagen (JPG, PNG, GIF, BMP, TIFF, AVIF, etc.)
 * a formato WebP usando un <canvas> en el navegador, sin dependencias externas.
 *
 * Props:
 *  - index        {number}   Índice del slot (para identificar la imagen en el padre)
 *  - onConverted  {function} Callback que recibe (index, webpFile, previewUrl)
 *                            • webpFile  → File listo para enviar con FormData
 *                            • previewUrl → URL.createObjectURL para mostrar preview
 *  - onRemove     {function} Callback que recibe (index) cuando el usuario elimina la imagen
 *  - preview      {string|null}  URL de preview actual (controlado por el padre)
 *  - selected     {boolean}      Si ya hay imagen seleccionada (controlado por el padre)
 *  - imageIcon    {string}       Ruta del ícono placeholder (pásalo como prop o usa el default)
 *  - quality      {number}       Calidad WebP 0–1 (default: 0.85)
 *  - maxWidth     {number}       Ancho máximo en px antes de redimensionar (default: 1920)
 *  - maxHeight    {number}       Alto máximo en px antes de redimensionar (default: 1920)
 *
 *  --- NUEVAS PROPS PARA TAMAÑO ESTÁNDAR ---
 *  - fixedWidth   {number|null}  Ancho fijo de salida (opcional). Si se usa con fixedHeight,
 *                                fuerza ese tamaño exacto.
 *  - fixedHeight  {number|null}  Alto fijo de salida (opcional).
 *  - fitMode      {string}       'cover' (recorta para llenar) o 'contain' (ajusta con fondo blanco)
 *                                Solo se aplica si fixedWidth y fixedHeight están definidos.
 *
 * Uso básico (tamaño estándar para tarjetas de producto):
 *  <ImageConverter
 *      index={0}
 *      fixedWidth={300}
 *      fixedHeight={300}
 *      fitMode="cover"
 *      quality={0.85}
 *      preview={imagenPreview[0]}
 *      selected={isImageSelected[0]}
 *      imageIcon={imageIcon}
 *      onConverted={(i, file, url) => { ... }}
 *      onRemove={(i) => { ... }}
 *  />
 */

import React, { useRef, useState } from 'react';

export default function ImageConverter({
    index,
    onConverted,
    onRemove,
    preview      = null,
    selected     = false,
    imageIcon    = '',
    quality      = 0.85,
    maxWidth     = 1920,
    maxHeight    = 1920,
    fixedWidth   = null,
    fixedHeight  = null,
    fitMode      = 'cover',   // 'cover' o 'contain'
}) {
    const inputRef   = useRef(null);
    const [converting, setConverting] = useState(false);

    // ─────────────────────────────────────────────────────────────
    // Convierte el File original a un File WebP (con tamaño opcional fijo)
    // ─────────────────────────────────────────────────────────────
    const convertToWebP = (file) =>
        new Promise((resolve, reject) => {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);

                let drawWidth, drawHeight, offsetX = 0, offsetY = 0;

                // Determinar dimensiones finales del canvas
                const canvasWidth  = (fixedWidth !== null && fixedHeight !== null) ? fixedWidth : maxWidth;
                const canvasHeight = (fixedWidth !== null && fixedHeight !== null) ? fixedHeight : maxHeight;

                // Caso 1: se pide tamaño estándar exacto (fixedWidth + fixedHeight)
                if (fixedWidth !== null && fixedHeight !== null) {
                    const imgRatio = img.naturalWidth / img.naturalHeight;
                    const targetRatio = fixedWidth / fixedHeight;

                    if (fitMode === 'cover') {
                        // Escalar para cubrir el área y centrar recorte
                        if (imgRatio > targetRatio) {
                            drawHeight = fixedHeight;
                            drawWidth = img.naturalWidth * (fixedHeight / img.naturalHeight);
                            offsetX = (drawWidth - fixedWidth) / 2;
                        } else {
                            drawWidth = fixedWidth;
                            drawHeight = img.naturalHeight * (fixedWidth / img.naturalWidth);
                            offsetY = (drawHeight - fixedHeight) / 2;
                        }
                    } else { // fitMode === 'contain'
                        // Escalar sin recortar, rellenar con fondo blanco
                        if (imgRatio > targetRatio) {
                            drawWidth = fixedWidth;
                            drawHeight = fixedWidth / imgRatio;
                            offsetY = (fixedHeight - drawHeight) / 2;
                        } else {
                            drawHeight = fixedHeight;
                            drawWidth = fixedHeight * imgRatio;
                            offsetX = (fixedWidth - drawWidth) / 2;
                        }
                    }
                } else {
                    // Caso 2: comportamiento original (respetar maxWidth/maxHeight sin forzar tamaño exacto)
                    let w = img.naturalWidth, h = img.naturalHeight;
                    if (w > maxWidth || h > maxHeight) {
                        const ratio = Math.min(maxWidth / w, maxHeight / h);
                        w = Math.round(w * ratio);
                        h = Math.round(h * ratio);
                    }
                    drawWidth = w;
                    drawHeight = h;
                }

                const canvas = document.createElement('canvas');
                canvas.width  = (fixedWidth !== null && fixedHeight !== null) ? fixedWidth : (drawWidth || maxWidth);
                canvas.height = (fixedWidth !== null && fixedHeight !== null) ? fixedHeight : (drawHeight || maxHeight);
                const ctx = canvas.getContext('2d');

                // Mejor calidad de escalado
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';

                // Rellenar fondo si usamos 'contain' y tamaño fijo
                if (fitMode === 'contain' && fixedWidth !== null && fixedHeight !== null) {
                    ctx.fillStyle = '#FFFFFF';  // Fondo blanco (puedes cambiarlo a transparente)
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                } else {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }

                ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

                canvas.toBlob(
                    (blob) => {
                        if (!blob) { reject(new Error('No se pudo convertir la imagen.')); return; }
                        const baseName = file.name.replace(/\.[^.]+$/, '');
                        const webpFile = new File([blob], `${baseName}.webp`, { type: 'image/webp' });
                        resolve(webpFile);
                    },
                    'image/webp',
                    quality,
                );
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('No se pudo leer la imagen.'));
            };

            img.src = objectUrl;
        });

    // ─────────────────────────────────────────────────────────────
    // Handler del <input type="file">
    // ─────────────────────────────────────────────────────────────
    const handleChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setConverting(true);
        try {
            const webpFile   = await convertToWebP(file);
            const previewUrl = URL.createObjectURL(webpFile);
            onConverted(index, webpFile, previewUrl);
        } catch (err) {
            console.error('ImageConverter error:', err);
            // Fallback: usar el archivo original si la conversión falla
            const previewUrl = URL.createObjectURL(file);
            onConverted(index, file, previewUrl);
        } finally {
            setConverting(false);
            e.target.value = '';
        }
    };

    const handleRemove = () => {
        if (inputRef.current) inputRef.current.value = '';
        onRemove(index);
    };

    // ─────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────
    return (
        <div className='image-input'>
            <input
                ref={inputRef}
                type='file'
                id={`imagen${index + 1}`}
                accept='image/*'
                onChange={handleChange}
                style={{ display: 'none' }}
            />

            <label
                htmlFor={selected ? undefined : `imagen${index + 1}`}
                className={`image-label ${selected ? 'selectedImage' : ''}`}
                style={{ cursor: selected ? 'default' : 'pointer' }}
            >
                {converting ? (
                    <span className='imgConverting'>Convirtiendo…</span>
                ) : selected ? (
                    <img src={preview} alt={`Vista previa ${index + 1}`} className='preview-image' />
                ) : (
                    <img src={imageIcon} alt='Seleccionar imagen' className='image-icon' />
                )}
            </label>

            {selected && !converting && (
                <button type='button' onClick={handleRemove} className='eliminar-imagen'>
                    Eliminar
                </button>
            )}
        </div>
    );
}